import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { uploadDocumentToTransformer } from "../../../../services/webServices";
import MessagePopup from "../../../popup-components/MessagePopup";
import SessionExpired from "../../../popup-components/SessionExpiredPopup";

const WebsiteAnalysisDrawer = ({ onClose, useNavigateBack = false }) => {
  const [url, setUrl] = useState("https://");
  const [errorMessage, setErrorMessage] = useState("");
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleBackClick = () => {
    if (useNavigateBack) navigate(-1);
    else onClose();
  };

  const handleUrlChange = (e) => {
    const input = e.target.value.trim();
    let fullUrl = input.startsWith("https://")
      ? input
      : `https://${input.replace(/^https?:\/\//, "")}`;
    setUrl(fullUrl);
  };

  //  URL validation
  const isValidUrl = (value) => {
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  };

  const handleAnalyze = async () => {
    if (!isValidUrl(url)) {
      setErrorMessage("Please enter the correct format of URL.");
      setIsErrorPopupOpen(true);
      return;
    }

    try {
      setLoading(true); // show spinner
      const response = await uploadDocumentToTransformer([], [url]);
      console.log("response after scraping the URL--->", response);
      navigate("/dashboard/qe-strategy", {
        state: { outputData: response.data.structuredCSV || response.data },
      });
    } catch (error) {
      console.error("Error analyzing:", error);
      if (error.response?.data?.message === "Your session has expired.") {
        setSessionExpired(true);
        setIsErrorPopupOpen(false);
      } else {
        const msg =
          error?.response?.data?.error ||
          error?.message ||
          "An unexpected error occurred. Please try again.";
        setErrorMessage(msg);
        setIsErrorPopupOpen(true);
      }
    } finally {
      setLoading(false); // hide spinner
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        p: 3,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            onClick={handleBackClick}
            edge="start"
            aria-label="back"
            sx={{ color: "black" }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: "bold", ml: 1 }}>
            Website Analysis
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <Typography variant="body2" sx={{ mb: 2 }}>
        Enter Website URL
      </Typography>

      <TextField
        fullWidth
        value={url.replace(/^https?:\/\//, "")}
        onChange={handleUrlChange}
        placeholder="www.qmentis.ai"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Typography sx={{ color: "gray" }}>https://</Typography>
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: 3 }}
      />

      {/* Buttons */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="outlined"
          disabled
          sx={{
            borderColor: "#3898fc",
            color: "#3898fc",
            textTransform: "none",
            fontWeight: 500,
            borderRadius: 2,
            px: 2,
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> Add URL
        </Button>

        <Button
          variant="contained"
          onClick={handleAnalyze}
          disabled={!isValidUrl(url) || loading} // disable only when invalid, not for styling
          sx={{
            backgroundColor: "#3898fc",
            textTransform: "none",
            fontWeight: 500,
            borderRadius: 2,
            px: 3,
            "&.Mui-disabled": {
              backgroundColor: "#3898fc", // keep blue even when disabled
              color: "white",
              opacity: 0.7,
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} sx={{ color: "white" }} />
              Analyzing URL
            </Box>
          ) : (
            "Analyze URL"
          )}
        </Button>
      </Box>

      {/* <Box
        sx={{
          position: "absolute",
          bottom: 16,
          right: 16,
          display: "flex",
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          sx={{
            borderColor: "#3898fc",
            color: "#3898fc",
            textTransform: "none",
            fontWeight: 500,
            borderRadius: 2,
            px: 3,
          }}
        >
          Back
        </Button>
      </Box> */}

      {isErrorPopupOpen && (
        <MessagePopup
          modalOpen={isErrorPopupOpen}
          okayHandler={() => setIsErrorPopupOpen(false)}
          message={errorMessage}
        />
      )}

      {sessionExpired && <SessionExpired />}
    </Box>
  );
};

export default WebsiteAnalysisDrawer;
